import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  Button,
  Checkbox,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { Redraft } from '@/components/Redraft';
import { logError } from '@/imports/logging/ClientLogger';
import { Prose } from '@/imports/ui/chakra/components/Prose';

import { CodeOfConductStep_TenantFragment } from './graphql-types/CodeOfConductStep_TenantFragment';

import { ActivationModalBody } from '../ActivationModalBody';
import { ActivationModalFooter } from '../ActivationModalFooter';
import { ActivationModalHeader } from '../ActivationModalHeader';
import { useAcceptCodeOfConduct } from './useAcceptCodeOfConduct';

const TENANT_FRAGMENT = gql`
  fragment CodeOfConductStep_TenantFragment on Tenant {
    _id
    codeOfConduct
  }
`;

export const CodeOfConductStep: React.FC<{
  tenant?: CodeOfConductStep_TenantFragment;
}> & {
  tenantFragment: DocumentNode;
} = (props) => {
  const toast = useToast();
  const [accepted, setAccepted] = React.useState(false);
  const [submitAcceptCodeOfConduct, submitAcceptCodeOfConductResult] =
    useAcceptCodeOfConduct(true);

  const checkboxText = useBreakpointValue({
    base: 'I read & accept this code',
    md: 'I read & accept this code of conduct',
  });

  const { handleSubmit } = useForm();

  if (!props.tenant) {
    return null;
  }

  return (
    <Box
      as="form"
      display="flex"
      flexDirection="column"
      overflow="auto"
      flexGrow="1"
      noValidate
      onSubmit={handleSubmit(async () => {
        try {
          await submitAcceptCodeOfConduct();
        } catch (err) {
          logError(err);
          toast({
            title:
              'Failed to accept the code of conduct, please refresh and try again.',
            status: 'error',
          });
        }
      })}
    >
      <ActivationModalHeader
        title="First things first, please read & accept our code of conduct."
        subtitle="The code of conduct ensures that we cultivate a community we all enjoy and find useful!"
      />
      <ActivationModalBody>
        <Box bg="gray.50" pt="1" pr="4" pl="4" pb="4" borderRadius="base">
          {props.tenant.codeOfConduct && (
            <Prose>
              <Redraft rawDraftContentState={props.tenant.codeOfConduct} />
            </Prose>
          )}
        </Box>
      </ActivationModalBody>
      <ActivationModalFooter>
        <Checkbox
          isChecked={accepted}
          onChange={() => setAccepted((oldValue) => !oldValue)}
        >
          {checkboxText}
        </Checkbox>
        <Button
          type="submit"
          isDisabled={!accepted}
          isLoading={submitAcceptCodeOfConductResult.loading}
        >
          Accept
        </Button>
      </ActivationModalFooter>
    </Box>
  );
};

CodeOfConductStep.tenantFragment = TENANT_FRAGMENT;
